import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface VendorPrice {
  retailer: string;
  url: string;
  price: number | null;
  inStock: boolean;
  lastUpdated: string;
}

export interface GPUPriceData {
  gpuId: string;
  vendors: VendorPrice[];
  lowestPrice: {
    price: number;
    retailer: string;
    url: string;
  } | null;
}

export function useGPUPrices(gpuIds?: string[]) {
  const [prices, setPrices] = useState<Record<string, GPUPriceData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('gpu_prices')
        .select('*')
        .order('retailer');

      if (gpuIds && gpuIds.length > 0) {
        query = query.in('gpu_id', gpuIds);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Transform data into grouped format
      const grouped: Record<string, GPUPriceData> = {};
      
      data?.forEach((row) => {
        if (!grouped[row.gpu_id]) {
          grouped[row.gpu_id] = {
            gpuId: row.gpu_id,
            vendors: [],
            lowestPrice: null,
          };
        }
        
        grouped[row.gpu_id].vendors.push({
          retailer: row.retailer,
          url: row.url,
          price: row.price ? Number(row.price) : null,
          inStock: row.in_stock,
          lastUpdated: row.last_updated,
        });
      });

      // Calculate lowest price for each GPU
      Object.values(grouped).forEach((gpu) => {
        const inStockVendors = gpu.vendors.filter(v => v.inStock && v.price !== null);
        if (inStockVendors.length > 0) {
          const lowest = inStockVendors.reduce((min, v) => 
            v.price! < min.price! ? v : min
          );
          gpu.lowestPrice = {
            price: lowest.price!,
            retailer: lowest.retailer,
            url: lowest.url,
          };
        }
      });

      setPrices(grouped);
    } catch (err) {
      console.error('Error fetching GPU prices:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch prices');
    } finally {
      setLoading(false);
    }
  }, [gpuIds]);

  const refreshPrices = useCallback(async () => {
    try {
      toast({
        title: 'Refreshing prices...',
        description: 'Fetching latest prices from retailers',
      });

      const { data, error } = await supabase.functions.invoke('fetch-gpu-prices', {
        body: gpuIds ? { gpuIds } : {},
      });

      if (error) throw error;

      toast({
        title: 'Prices updated!',
        description: `Updated ${data.updated} price records`,
      });

      // Refetch from database
      await fetchPrices();
    } catch (err) {
      console.error('Error refreshing prices:', err);
      toast({
        title: 'Error refreshing prices',
        description: err instanceof Error ? err.message : 'Failed to refresh',
        variant: 'destructive',
      });
    }
  }, [gpuIds, fetchPrices, toast]);

  // Initial fetch
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Subscribe to real-time updates
  useEffect(() => {
    const channel = supabase
      .channel('gpu-prices-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'gpu_prices',
        },
        (payload) => {
          console.log('Price update received:', payload);
          // Refetch all prices on any change
          fetchPrices();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchPrices]);

  return {
    prices,
    loading,
    error,
    refreshPrices,
    refetch: fetchPrices,
  };
}

export function useGPUPrice(gpuId: string) {
  const { prices, loading, error, refreshPrices } = useGPUPrices([gpuId]);
  return {
    priceData: prices[gpuId] || null,
    loading,
    error,
    refreshPrices,
  };
}
